import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, CATEGORIES } from "../lib/api";
import HeadlineList from "../components/HeadlineList";
import PressReleaseList from "../components/PressReleaseList";
import PollAverageCard from "../components/PollAverageCard";
import NewsletterCTA from "../components/NewsletterCTA";
import ImageOfDay from "../components/ImageOfDay";

const SECTION_LIST = [
  { slug: "portada", label: "Portada", anchor: "#portada" },
  ...CATEGORIES.map((c) => ({ slug: c.slug, label: c.label, anchor: `#${c.slug}` })),
  { slug: "comunicados", label: "Comunicados Gob. de México", anchor: "#comunicados" },
  { slug: "dof", label: "Diario Oficial de la Federación", anchor: "#dof" },
  { slug: "encuestas", label: "Encuestas", anchor: "#encuestas" },
];

const dateLabelEs = () => {
  const d = new Date();
  return d.toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" }).toUpperCase();
};

function SidebarNav({ counts }) {
  return (
    <nav data-testid="sidebar-nav">
      <p className="section-eyebrow mb-3">Secciones</p>
      <ul className="border-t border-ink">
        {SECTION_LIST.map((s) => (
          <li key={s.slug} className="border-b border-rule">
            <a
              href={s.anchor}
              className="flex items-baseline justify-between gap-2 py-2 px-1 hover:bg-ink/5 transition-colors"
              data-testid={`side-${s.slug}`}
            >
              <span className="font-sans text-[13px] font-semibold uppercase tracking-wider leading-tight">
                {s.label}
              </span>
              {counts && counts[s.slug] != null && (
                <span className="font-mono text-[11px] text-sub shrink-0">{counts[s.slug]}</span>
              )}
            </a>
          </li>
        ))}
      </ul>
      <Link
        to="/encuestas"
        className="mt-4 block text-center border border-ink py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors"
        data-testid="side-link-polls"
      >
        Dashboard de Encuestas →
      </Link>
    </nav>
  );
}

function PollListBlock({ data, testId }) {
  if (!data || !data.results?.length) {
    return <p className="text-sm text-sub" data-testid={`${testId}-empty`}>Sin datos.</p>;
  }
  return (
    <ul className="text-sm divide-y divide-rule" data-testid={testId}>
      {data.results.map((r) => (
        <li key={r.candidate} className="flex items-baseline justify-between py-1.5">
          <span className="font-sans">{r.candidate}</span>
          <span className="font-mono font-semibold">{r.average.toFixed(1)}%</span>
        </li>
      ))}
    </ul>
  );
}

function DOFList({ items, testId = "dof-list" }) {
  if (!items?.length) return <p className="py-3 text-sm text-sub" data-testid={`${testId}-empty`}>Sin publicaciones.</p>;
  return (
    <ul className="border-t border-rule" data-testid={testId}>
      {items.map((d) => (
        <li key={d.id}>
          <a
            href={d.url}
            target="_blank"
            rel="noopener noreferrer"
            className="headline-link group flex items-start gap-2 px-1"
            data-testid={`dof-${d.id}`}
          >
            <span className="source-badge mt-[3px]" style={{ background: "#8B0000" }} title="Diario Oficial">
              DOF
            </span>
            <span className="flex-1 text-[14px] leading-snug tracking-news">
              {d.title}
              {d.section && (
                <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-sub">
                  · {d.section}
                </span>
              )}
            </span>
            <span className="ml-1 text-sub text-[11px] mt-[2px]" aria-hidden>↗</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

export default function Home() {
  const [byCat, setByCat] = useState({});
  const [opinion, setOpinion] = useState([]);
  const [press, setPress] = useState([]);
  const [dof, setDof] = useState([]);
  const [approval, setApproval] = useState(null);
  const [partyAvg, setPartyAvg] = useState(null);
  const [govAvg, setGovAvg] = useState(null);
  const [cand2030, setCand2030] = useState(null);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    (async () => {
      const cats = {};
      await Promise.all(
        CATEGORIES.map(async (c) => {
          // Opinion: 10 entries with strong source diversity (max 1 per paper).
          // Other sections: 7+ entries, max 2 per paper for natural balance.
          const limit = c.slug === "opinion" ? 10 : c.slug === "congresos-estados" ? 12 : 7;
          const max_per_source = c.slug === "opinion" ? 1 : 2;
          // Estados + Congresos de los Estados sí queremos cobertura regional;
          // las demás (Portada/Presidencia/Congreso/Opinión/Internacional) sólo nacionales.
          const national_only = !["estados", "congresos-estados"].includes(c.slug);
          // La sección Congreso es la única que mezcla coverage editorial con
          // boletines oficiales (Cámara de Diputados, etc.). En las demás secciones
          // los institucionales jamás aparecen.
          const include_institutional = c.slug === "congreso";
          const { data } = await http.get("/articles", {
            params: { category: c.slug, limit, diverse: true, max_per_source, national_only, include_institutional },
          });
          cats[c.slug] = data;
        })
      );
      setByCat(cats);
      setOpinion(cats.opinion || []);

      const [feat, pr, dofRes, app, pa, ga, c30] = await Promise.all([
        http.get("/articles", { params: { featured: true, limit: 6 } }),
        http.get("/press-releases", { params: { limit: 30 } }),
        http.get("/dof", { params: { limit: 8 } }),
        http.get("/polls/average", { params: { topic: "approval", last_n: 5 } }),
        http.get("/polls/average", { params: { topic: "party_approval", last_n: 5 } }),
        http.get("/polls/average", { params: { topic: "governor_approval", last_n: 2 } }),
        http.get("/polls/average", { params: { topic: "candidates_2030", last_n: 5 } }),
      ]);
      setFeatured(feat.data);
      setPress(pr.data);
      setDof(dofRes.data);
      setApproval(app.data);
      setPartyAvg(pa.data);
      setGovAvg(ga.data);
      setCand2030(c30.data);
    })();
  }, []);

  // Hydrate Open Graph meta tags with the curated image-of-day + lead headline.
  // Crawlers like Facebook and LinkedIn re-fetch and read these.
  useEffect(() => {
    let cancelled = false;
    http.get("/og-data").then(({ data }) => {
      if (cancelled || !data) return;
      const setMeta = (sel, content) => {
        if (!content) return;
        const el = document.querySelector(sel);
        if (el) el.setAttribute("content", content);
      };
      if (data.title) document.title = data.title;
      setMeta('meta[name="description"]', data.description);
      setMeta('meta[property="og:title"]', data.title);
      setMeta('meta[property="og:description"]', data.description);
      setMeta('meta[property="og:image"]', data.image_url);
      setMeta('meta[property="og:url"]', data.site);
      setMeta('meta[name="twitter:title"]', data.title);
      setMeta('meta[name="twitter:description"]', data.description);
      setMeta('meta[name="twitter:image"]', data.image_url);
    }).catch(() => { /* keep static defaults */ });
    return () => { cancelled = true; };
  }, []);

  const counts = {
    portada: featured.length,
    ...Object.fromEntries(CATEGORIES.map((c) => [c.slug, (byCat[c.slug] || []).length])),
    comunicados: press.length,
    dof: dof.length,
    encuestas: 5,
  };

  const lead = featured[0];
  const subleads = featured.slice(1, 5); // 4 subleads

  return (
    <div data-testid="home-page" className="animate-fade-in">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-6 grid grid-cols-12 gap-6 lg:gap-8">
        {/* COL 1 — SIDEBAR NAV + IMAGEN DEL DÍA (ambos sticky juntos) */}
        <aside className="col-span-12 lg:col-span-2">
          <div className="lg:sticky lg:top-4 self-start" data-testid="sidebar-sticky">
            <SidebarNav counts={counts} />
            <ImageOfDay />
          </div>
        </aside>

        {/* COL 2 — MAIN STREAM */}
        <section className="col-span-12 lg:col-span-7 min-w-0">
          {/* Portada / lead — gran encabezado, sin imagen */}
          <div id="portada" className="scroll-mt-24">
            <div className="dbl-rule pt-3 pb-2 mb-6">
              <p className="font-mono text-[14px] sm:text-[16px] font-bold uppercase tracking-[0.32em] text-ink">
                Portada · {dateLabelEs()}
              </p>
            </div>
            {lead ? (
              <a
                href={lead.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
                data-testid="lead-article"
              >
                <span className="source-badge">{lead.source_code}</span>
                <span className="font-mono text-[11px] uppercase tracking-widest text-sub ml-1">
                  {lead.source_name} ↗
                </span>
                <h2 className="font-serif-display tracking-news text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.02] mt-3 group-hover:text-rojo transition-colors">
                  {lead.title}
                </h2>
                {lead.summary && (
                  <p className="mt-5 text-lg sm:text-xl text-sub leading-relaxed font-sans max-w-3xl">
                    {lead.summary}
                  </p>
                )}
              </a>
            ) : (
              <div className="h-32 border border-rule" />
            )}
            {/* 4 subleads — todas nacionales */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mt-8 pt-6 border-t border-rule">
              {subleads.map((a) => (
                <a
                  key={a.id}
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                  data-testid={`sublead-${a.id}`}
                >
                  <span className="source-badge">{a.source_code}</span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-sub ml-1">
                    {a.source_name}
                  </span>
                  <h3 className="font-serif-display tracking-news text-xl sm:text-2xl font-semibold mt-1 leading-tight group-hover:text-rojo transition-colors">
                    {a.title}
                  </h3>
                  {a.summary && (
                    <p className="mt-1 text-sm text-sub leading-snug line-clamp-2">{a.summary}</p>
                  )}
                </a>
              ))}
            </div>
          </div>

          {/* Categorías densas */}
          <div className="mt-12">
            {CATEGORIES.filter((c) => c.slug !== "opinion").map((c) => (
              <div key={c.slug} id={c.slug} className="scroll-mt-24 mt-10 first:mt-0">
                <div className="flex items-baseline justify-between border-b-2 border-ink pb-1 mb-2">
                  <h3 className="font-serif-display text-2xl sm:text-3xl text-ink">{c.label}</h3>
                  <Link
                    to={`/categoria/${c.slug}`}
                    className="font-mono text-[11px] uppercase tracking-widest text-sub hover:text-rojo"
                    data-testid={`cat-more-${c.slug}`}
                  >
                    Ver todo →
                  </Link>
                </div>
                <HeadlineList items={byCat[c.slug] || []} testId={`headlines-${c.slug}`} />
              </div>
            ))}
          </div>

          {/* Opinión curada */}
          <div id="opinion" className="scroll-mt-24 mt-12">
            <div className="flex items-baseline justify-between border-b-2 border-ink pb-1 mb-2">
              <h3 className="font-serif-display text-2xl sm:text-3xl">Opinión · 5 plumas del día</h3>
              <Link to="/categoria/opinion" className="font-mono text-[11px] uppercase tracking-widest text-sub hover:text-rojo">
                Más →
              </Link>
            </div>
            <HeadlineList items={opinion} testId="headlines-opinion" />
          </div>

          {/* Newsletter inline CTA */}
          <NewsletterCTA source="home-inline" variant="inline" />

          {/* Comunicados Gobierno de México */}
          <div id="comunicados" className="scroll-mt-24 mt-12">
            <div className="flex items-baseline justify-between border-b-2 border-ink pb-1 mb-2">
              <h3 className="font-serif-display text-2xl sm:text-3xl">Comunicados Gobierno de México</h3>
              <span className="font-mono text-[11px] uppercase tracking-widest text-sub">
                Secretarías de Estado
              </span>
            </div>
            <PressReleaseList items={(press || []).slice(0, 5)} testId="home-press-list" />
            {press && press.length > 0 && (
              <div className="mt-2 flex justify-end">
                <Link
                  to="/comunicados"
                  className="font-mono text-[11px] uppercase tracking-widest text-rojo hover:underline"
                  data-testid="home-press-see-all"
                >
                  Ver todos los comunicados →
                </Link>
              </div>
            )}
          </div>

          {/* Diario Oficial de la Federación */}
          <div id="dof" className="scroll-mt-24 mt-12">
            <div className="flex items-baseline justify-between border-b-2 border-ink pb-1 mb-2">
              <h3 className="font-serif-display text-2xl sm:text-3xl">Diario Oficial de la Federación</h3>
              <a
                href="https://www.dof.gob.mx"
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-[11px] uppercase tracking-widest text-sub hover:text-rojo"
                data-testid="dof-source-link"
              >
                Ver edición completa ↗
              </a>
            </div>
            <DOFList items={dof} testId="home-dof-list" />
          </div>
        </section>

        {/* COL 3 — POLLS */}
        <aside id="encuestas" className="col-span-12 lg:col-span-3 scroll-mt-24" data-testid="home-poll-column">
          <p className="section-eyebrow">Concentrado · Encuestas</p>

          <div className="mt-3 space-y-5">
            <PollAverageCard data={approval} title="Aprobación Sheinbaum" />

            <div className="border border-ink bg-paper p-3" data-testid="poll-list-party">
              <p className="font-mono text-[11px] uppercase tracking-widest text-sub border-b border-rule pb-1 mb-2">
                Aprobación por Partido
              </p>
              <PollListBlock data={partyAvg} testId="party-list" />
            </div>

            <div className="border border-ink bg-paper p-3" data-testid="poll-list-governor">
              <p className="font-mono text-[11px] uppercase tracking-widest text-sub border-b border-rule pb-1 mb-2">
                Aprobación por Gobernador
              </p>
              <PollListBlock data={govAvg} testId="governor-list" />
            </div>

            <div className="border border-ink bg-paper p-3" data-testid="poll-list-2030">
              <p className="font-mono text-[11px] uppercase tracking-widest text-sub border-b border-rule pb-1 mb-2">
                Candidatos Presidenciales · 2030
              </p>
              <PollListBlock data={cand2030} testId="candidates-2030-list" />
            </div>

            <Link
              to="/encuestas"
              className="block text-center border border-ink py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors"
              data-testid="link-poll-dashboard"
            >
              Gráfica + tabla completa →
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
